import * as React from "react";
import { injectIntl, WrappedComponentProps } from "react-intl";
import { Table } from "antd";
import { Currency, FoundTrade } from "swan-public-api-client";
import { SpinProps } from "antd/lib/spin";
import { PaginationConfig, ColumnProps, TableLocale } from "antd/lib/table";
import {
  considerationRenderer,
  priceWithCurrencyViaRecordRenderer,
  dateTimeRenderer,
  dateRenderer,
  quantityDirectionRenderer,
  tradeSubTypeRenderer
} from "../../Grid/Renderers";
import {
  getConsiderationSorter,
  getDateSorter,
  getReferenceSorter,
} from "../../Grid/Sorters";
import { connect } from "react-redux";
import { AppState } from "../../Store/RootReducer";
import { tradeSubTypeToStringConverter } from "../../Localisation/Converters";

interface PropsFromState {
  currencies: Map<number, Currency>;
}

interface Props extends WrappedComponentProps, PropsFromState {
  loading?: boolean | SpinProps;
  bordered?: boolean;
  locale?: TableLocale;
  pagination?: PaginationConfig | false;
  trades: FoundTrade[];
}

class SpotFxTradeHistoryGridCmp extends React.PureComponent<Props> {
  public render() {
    return (
      <Table
        loading={this.props.loading}
        bordered={this.props.bordered}
        pagination={this.props.pagination}
        dataSource={this.props.trades}
        rowKey={trade => trade.InstructionKey.Key}
        locale={this.props.locale}
        columns={this.getColumns()}
      />
    );
  }

  private getColumns() {
    const columns: Array<ColumnProps<FoundTrade>> = [
      {
        dataIndex: "Reference",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.reference" }),
        sorter: getReferenceSorter(x => x.Reference),
        sortDirections: ["ascend", "descend"],
        defaultSortOrder: "ascend"
      },
      {
        dataIndex: "MarketName",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.marketName" }),
        sorter: (a, b) => a.MarketName < b.MarketName ? -1 : 1,
        sortDirections: ["ascend", "descend"]
      },
      {
        dataIndex: "SubType",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.subType" }),
        sorter: (a, b) => {
          const aStr = tradeSubTypeToStringConverter(a.SubType, this.props.intl);
          const bStr = tradeSubTypeToStringConverter(b.SubType, this.props.intl);
          return aStr.localeCompare(bStr);
        },
        sortDirections: ["ascend", "descend"],
        render: tradeSubTypeRenderer(x => x.SubType, this.props.intl),
      },
      {
        dataIndex: "OriginalQuantity",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.direction" }),
        sorter: (a, b) => a.OriginalQuantity < b.OriginalQuantity ? -1 : 1,
        sortDirections: ["ascend", "descend"],
        render: quantityDirectionRenderer(x => x.OriginalQuantity, this.props.intl)
      },
      {
        dataIndex: "Consideration.BaseConsideration.Value",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.baseCurrencyConsideration" }),
        sorter: getConsiderationSorter(x => x.Consideration.BaseConsideration),
        sortDirections: ["ascend", "descend"],
        render: considerationRenderer(
          x => x.Consideration.BaseConsideration,
          this.props.currencies
        ),
        align: "right"
      },
      {
        dataIndex: "Consideration.DeliveryConsideration.Value",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.deliveryCurrencyConsideration" }),
        sorter: getConsiderationSorter(x => x.Consideration.DeliveryConsideration),
        sortDirections: ["ascend", "descend"],
        render: considerationRenderer(
          x => x.Consideration.DeliveryConsideration,
          this.props.currencies
        ),
        align: "right"
      },
      {
        dataIndex: "BookingPrice",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.bookingPrice" }),
        sorter: (a, b) => a.BookingPrice < b.BookingPrice ? -1 : 1,
        sortDirections: ["ascend", "descend"],
        render: priceWithCurrencyViaRecordRenderer(x => x.BookingPrice, _ => undefined, this.props.currencies),
        align: "right"
      },
      {
        dataIndex: "BookingTime",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.bookingTime" }),
        sorter: getDateSorter(x => x.BookingTime),
        sortDirections: ["ascend", "descend"],
        render: dateTimeRenderer(x => x.BookingTime)
      },
      {
        dataIndex: "ValueDate",
        title: this.props.intl.formatMessage({ id: "tradeHistoryGrid.valueDate" }),
        sorter: getDateSorter(x => x.ValueDate),
        sortDirections: ["ascend", "descend"],
        render: dateRenderer(x => x.ValueDate)
      }
    ];

    return columns;
  }
}

const SpotFxTradeHistoryGridWithIntl = injectIntl(SpotFxTradeHistoryGridCmp);

const mapStateToProps = (state: AppState): PropsFromState => ({
  currencies: new Map<number, Currency>(
    state.activeTradingAccount.account!.Currencies.map(x => [parseInt(x.Key, 10), x])
  )
});

export const SpotFxTradeHistoryGrid = connect(mapStateToProps)(
  SpotFxTradeHistoryGridWithIntl
);
